import React, { useState, useEffect, useCallback } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import { useGym } from '../contexts/GymContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  Brain, 
  Calendar, 
  TrendingUp, 
  TrendingDown,
  DollarSign,
  Target,
  AlertTriangle,
  CheckCircle2,
  Clock,
  Trash2,
  RefreshCw,
  ChevronDown,
  Sparkles
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../services/api';
import ReactMarkdown from 'react-markdown';
import { formatCurrency } from '../utils/formatters';

const months = [
  { value: 1, label: 'Janeiro' },
  { value: 2, label: 'Fevereiro' },
  { value: 3, label: 'Março' },
  { value: 4, label: 'Abril' },
  { value: 5, label: 'Maio' },
  { value: 6, label: 'Junho' },
  { value: 7, label: 'Julho' },
  { value: 8, label: 'Agosto' },
  { value: 9, label: 'Setembro' },
  { value: 10, label: 'Outubro' },
  { value: 11, label: 'Novembro' },
  { value: 12, label: 'Dezembro' }
];

export const FinancialDirectorPage = () => {
  const { selectedGym } = useGym();
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [closings, setClosings] = useState([]);
  const [selectedClosing, setSelectedClosing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [deleting, setDeleting] = useState(null);

  const loadClosings = useCallback(async () => {
    if (!selectedGym) return;
    try {
      setLoading(true);
      const response = await api.get(`/cfo/closings?gym_id=${selectedGym.id}`);
      setClosings(response.data.closings || []);
      
      // Auto-select the most recent closing if available
      if (response.data.closings?.length > 0) {
        setSelectedClosing(response.data.closings[0]);
      }
    } catch (error) {
      toast.error('Erro ao carregar histórico de fechamentos');
    } finally {
      setLoading(false);
    }
  }, [selectedGym]);

  useEffect(() => {
    if (selectedGym) {
      loadClosings();
    }
  }, [selectedGym, loadClosings]);

  const handleCloseMonth = async () => {
    if (!selectedGym) {
      toast.error('Selecione uma unidade');
      return;
    }

    // Check if this month is already closed
    const yearMonth = `${selectedYear}-${String(selectedMonth).padStart(2, '0')}`;
    const existingClosing = closings.find(c => c.year_month === yearMonth);
    if (existingClosing) {
      toast.error('Este mês já foi fechado. Exclua o fechamento anterior para gerar novamente.');
      setSelectedClosing(existingClosing);
      return;
    }

    try {
      setGenerating(true);
      toast.info('Gerando análise do Diretor Financeiro... Isso pode levar alguns segundos.');
      
      const response = await api.post(`/cfo/close-month?gym_id=${selectedGym.id}&year=${selectedYear}&month=${selectedMonth}`);
      
      toast.success('Mês fechado com sucesso!');
      
      // Add the new closing to the list and select it
      const newClosing = response.data.closing;
      setClosings([newClosing, ...closings]);
      setSelectedClosing(newClosing);
    } catch (error) {
      const message = error.response?.data?.detail || 'Erro ao gerar análise';
      toast.error(message);
    } finally {
      setGenerating(false);
    }
  };

  const handleDeleteClosing = async (closingId) => {
    if (!confirm('Tem certeza que deseja excluir este fechamento? Você poderá gerar uma nova análise para este mês.')) {
      return;
    }

    try {
      setDeleting(closingId);
      await api.delete(`/cfo/closing/${closingId}`);
      toast.success('Fechamento excluído. Você pode gerar uma nova análise.');
      
      // Remove from list
      setClosings(closings.filter(c => c.id !== closingId));
      
      // Clear selected if it was the deleted one
      if (selectedClosing?.id === closingId) {
        setSelectedClosing(closings.length > 1 ? closings.find(c => c.id !== closingId) : null);
      }
    } catch (error) {
      toast.error('Erro ao excluir fechamento');
    } finally {
      setDeleting(null);
    }
  };

  const selectClosingFromHistory = (closing) => {
    setSelectedClosing(closing);
    // Update the selectors to match
    const [year, month] = closing.year_month.split('-');
    setSelectedYear(parseInt(year));
    setSelectedMonth(parseInt(month));
  };

  // Check if current selected month is already closed
  const currentYearMonth = `${selectedYear}-${String(selectedMonth).padStart(2, '0')}`;
  const isMonthClosed = closings.some(c => c.year_month === currentYearMonth);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="flex items-center gap-3">
            <Brain className="w-8 h-8 animate-pulse" style={{ color: 'var(--color-primary)' }} />
            <p className="text-xl font-heading" style={{ color: 'var(--color-primary)' }}>Carregando...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2.5 rounded-xl" style={{ background: 'linear-gradient(135deg, var(--color-primary), var(--color-secondary))' }}>
                <Brain className="w-7 h-7 text-black" />
              </div>
              <h1 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold uppercase tracking-tight" style={{ color: 'var(--app-text-primary)' }}>
                Diretor Financeiro
              </h1>
            </div>
            <p className="text-sm lg:text-base" style={{ color: 'var(--app-text-secondary)' }}>
              Análise estratégica mensal com Inteligência Artificial
            </p>
          </div>
        </div>

        {/* Month/Year Selector & Action */}
        <Card className="glass-panel" data-testid="month-selector-card">
          <CardContent className="p-6">
            <div className="flex flex-col lg:flex-row lg:items-end gap-4">
              <div className="flex-1 grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: 'var(--app-text-secondary)' }}>
                    Mês
                  </label>
                  <select
                    data-testid="cfo-month-selector"
                    value={selectedMonth}
                    onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                    className="input-field w-full px-4 py-3 font-medium"
                  >
                    {months.map(m => (
                      <option key={m.value} value={m.value}>{m.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: 'var(--app-text-secondary)' }}>
                    Ano
                  </label>
                  <select
                    data-testid="cfo-year-selector"
                    value={selectedYear}
                    onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                    className="input-field w-full px-4 py-3 font-medium"
                  >
                    {[2024, 2025, 2026].map(year => (
                      <option key={year} value={year}>{year}</option>
                    ))}
                  </select>
                </div>
              </div>
              <Button
                data-testid="close-month-button"
                onClick={handleCloseMonth}
                disabled={generating || isMonthClosed}
                className="w-full lg:w-auto px-8 py-3 font-semibold text-black rounded-xl transition-all duration-300 disabled:opacity-50"
                style={{ 
                  background: isMonthClosed 
                    ? 'var(--app-border)' 
                    : 'linear-gradient(135deg, var(--color-primary), var(--color-secondary))',
                  boxShadow: isMonthClosed ? 'none' : '0 4px 20px color-mix(in srgb, var(--color-primary) 30%, transparent)'
                }}
              >
                {generating ? (
                  <span className="flex items-center gap-2">
                    <RefreshCw className="w-5 h-5 animate-spin" />
                    Analisando...
                  </span>
                ) : isMonthClosed ? (
                  <span className="flex items-center gap-2">
                    <CheckCircle2 className="w-5 h-5" />
                    Mês Fechado
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <Sparkles className="w-5 h-5" />
                    Finalizar Mês
                  </span>
                )}
              </Button>
            </div>
            
            {isMonthClosed && (
              <p className="mt-3 text-sm flex items-center gap-2" style={{ color: 'var(--color-warning)' }}>
                <AlertTriangle className="w-4 h-4" />
                Este mês já foi fechado. Exclua o fechamento para gerar novamente.
              </p>
            )}
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* History Panel */}
          <Card className="glass-panel lg:col-span-4" data-testid="history-panel">
            <CardHeader>
              <CardTitle className="text-lg font-heading uppercase flex items-center gap-2" style={{ color: 'var(--app-text-primary)' }}>
                <Clock className="w-5 h-5" style={{ color: 'var(--color-secondary)' }} />
                Histórico
              </CardTitle>
            </CardHeader>
            <CardContent>
              {closings.length === 0 ? (
                <div className="text-center py-8">
                  <Calendar className="w-12 h-12 mx-auto mb-3 opacity-30" style={{ color: 'var(--app-text-secondary)' }} />
                  <p style={{ color: 'var(--app-text-secondary)' }}>Nenhum mês fechado ainda</p>
                  <p className="text-xs mt-1" style={{ color: 'var(--app-text-secondary)' }}>
                    Selecione um mês e clique em "Finalizar Mês"
                  </p>
                </div>
              ) : (
                <div className="space-y-2 max-h-[400px] overflow-y-auto custom-scrollbar">
                  {closings.map((closing) => {
                    const isSelected = selectedClosing?.id === closing.id;
                    const data = closing.financial_data || {};
                    return (
                      <div
                        key={closing.id}
                        data-testid={`closing-${closing.year_month}`}
                        role="button"
                        tabIndex={0}
                        onClick={() => selectClosingFromHistory(closing)}
                        onKeyDown={(e) => e.key === 'Enter' && selectClosingFromHistory(closing)}
                        className={`w-full text-left p-4 rounded-xl border transition-all duration-200 group cursor-pointer ${isSelected ? 'ring-2' : ''}`}
                        style={{
                          backgroundColor: isSelected 
                            ? 'color-mix(in srgb, var(--color-primary) 10%, transparent)' 
                            : 'color-mix(in srgb, var(--app-text-primary) 3%, transparent)',
                          borderColor: isSelected ? 'var(--color-primary)' : 'var(--app-border)',
                          ringColor: 'var(--color-primary)'
                        }}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-semibold" style={{ color: 'var(--app-text-primary)' }}>
                            {months.find(m => m.value === closing.month)?.label || closing.month}/{closing.year}
                          </span>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteClosing(closing.id);
                            }}
                            disabled={deleting === closing.id}
                            className="opacity-0 group-hover:opacity-100 p-1.5 rounded-lg transition-all hover:bg-red-500/20"
                            style={{ color: 'var(--color-danger)' }}
                          >
                            {deleting === closing.id ? (
                              <RefreshCw className="w-4 h-4 animate-spin" />
                            ) : (
                              <Trash2 className="w-4 h-4" />
                            )}
                          </button>
                        </div>
                        <div className="grid grid-cols-2 gap-2 text-xs">
                          <div className="flex items-center gap-1">
                            <TrendingUp className="w-3 h-3" style={{ color: 'var(--color-primary)' }} />
                            <span style={{ color: 'var(--app-text-secondary)' }}>
                              {formatCurrency(data.receita_total)}
                            </span>
                          </div>
                          <div className="flex items-center gap-1">
                            <DollarSign className="w-3 h-3" style={{ color: data.lucro >= 0 ? 'var(--color-primary)' : 'var(--color-danger)' }} />
                            <span style={{ color: data.lucro >= 0 ? 'var(--color-primary)' : 'var(--color-danger)' }}>
                              {formatCurrency(data.lucro)}
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          {/* AI Analysis Panel */}
          <Card className="glass-panel lg:col-span-8" data-testid="analysis-panel">
            <CardHeader>
              <CardTitle className="text-lg font-heading uppercase flex items-center gap-2" style={{ color: 'var(--app-text-primary)' }}>
                <Brain className="w-5 h-5" style={{ color: 'var(--color-primary)' }} />
                {selectedClosing ? (
                  <span>
                    Análise de {months.find(m => m.value === selectedClosing.month)?.label}/{selectedClosing.year}
                  </span>
                ) : (
                  <span>Análise do Diretor Financeiro</span>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {!selectedClosing ? (
                <div className="text-center py-16">
                  <div className="w-20 h-20 rounded-full mx-auto mb-4 flex items-center justify-center" 
                       style={{ background: 'linear-gradient(135deg, color-mix(in srgb, var(--color-primary) 20%, transparent), color-mix(in srgb, var(--color-secondary) 20%, transparent))' }}>
                    <Brain className="w-10 h-10" style={{ color: 'var(--color-primary)' }} />
                  </div>
                  <h3 className="text-xl font-semibold mb-2" style={{ color: 'var(--app-text-primary)' }}>
                    Nenhuma análise disponível
                  </h3>
                  <p className="max-w-md mx-auto" style={{ color: 'var(--app-text-secondary)' }}>
                    Selecione um mês no histórico ou clique em "Finalizar Mês" para gerar uma nova análise estratégica do seu Diretor Financeiro.
                  </p>
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Financial Summary Cards */}
                  {selectedClosing.financial_data && (
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                      <div className="p-4 rounded-xl border" style={{ backgroundColor: 'color-mix(in srgb, var(--color-primary) 5%, transparent)', borderColor: 'var(--app-border)' }}>
                        <div className="flex items-center gap-2 mb-1">
                          <TrendingUp className="w-4 h-4" style={{ color: 'var(--color-primary)' }} />
                          <span className="text-xs font-medium" style={{ color: 'var(--app-text-secondary)' }}>Receita</span>
                        </div>
                        <p className="text-lg font-bold font-numbers" style={{ color: 'var(--color-primary)' }}>
                          {formatCurrency(selectedClosing.financial_data.receita_total)}
                        </p>
                        {selectedClosing.financial_data.variacao_receita_vs_mes_anterior !== 0 && (
                          <p className="text-xs mt-1" style={{ 
                            color: selectedClosing.financial_data.variacao_receita_vs_mes_anterior >= 0 
                              ? 'var(--color-primary)' 
                              : 'var(--color-danger)' 
                          }}>
                            {selectedClosing.financial_data.variacao_receita_vs_mes_anterior >= 0 ? '+' : ''}
                            {selectedClosing.financial_data.variacao_receita_vs_mes_anterior.toFixed(1)}% vs mês anterior
                          </p>
                        )}
                      </div>
                      
                      <div className="p-4 rounded-xl border" style={{ backgroundColor: 'color-mix(in srgb, var(--color-danger) 5%, transparent)', borderColor: 'var(--app-border)' }}>
                        <div className="flex items-center gap-2 mb-1">
                          <TrendingDown className="w-4 h-4" style={{ color: 'var(--color-danger)' }} />
                          <span className="text-xs font-medium" style={{ color: 'var(--app-text-secondary)' }}>Despesas</span>
                        </div>
                        <p className="text-lg font-bold font-numbers" style={{ color: 'var(--color-danger)' }}>
                          {formatCurrency(selectedClosing.financial_data.despesas_total)}
                        </p>
                        {selectedClosing.financial_data.variacao_despesas_vs_mes_anterior !== 0 && (
                          <p className="text-xs mt-1" style={{ 
                            color: selectedClosing.financial_data.variacao_despesas_vs_mes_anterior <= 0 
                              ? 'var(--color-primary)' 
                              : 'var(--color-danger)' 
                          }}>
                            {selectedClosing.financial_data.variacao_despesas_vs_mes_anterior >= 0 ? '+' : ''}
                            {selectedClosing.financial_data.variacao_despesas_vs_mes_anterior.toFixed(1)}% vs mês anterior
                          </p>
                        )}
                      </div>
                      
                      <div className="p-4 rounded-xl border" style={{ backgroundColor: 'color-mix(in srgb, var(--color-secondary) 5%, transparent)', borderColor: 'var(--app-border)' }}>
                        <div className="flex items-center gap-2 mb-1">
                          <DollarSign className="w-4 h-4" style={{ color: 'var(--color-secondary)' }} />
                          <span className="text-xs font-medium" style={{ color: 'var(--app-text-secondary)' }}>Lucro</span>
                        </div>
                        <p className="text-lg font-bold font-numbers" style={{ 
                          color: selectedClosing.financial_data.lucro >= 0 
                            ? 'var(--color-primary)' 
                            : 'var(--color-danger)' 
                        }}>
                          {formatCurrency(selectedClosing.financial_data.lucro)}
                        </p>
                        <p className="text-xs mt-1" style={{ color: 'var(--app-text-secondary)' }}>
                          Margem: {selectedClosing.financial_data.margem_percentual?.toFixed(1) || 0}%
                        </p>
                      </div>
                      
                      <div className="p-4 rounded-xl border" style={{ backgroundColor: 'color-mix(in srgb, var(--color-warning) 5%, transparent)', borderColor: 'var(--app-border)' }}>
                        <div className="flex items-center gap-2 mb-1">
                          <Target className="w-4 h-4" style={{ color: 'var(--color-warning)' }} />
                          <span className="text-xs font-medium" style={{ color: 'var(--app-text-secondary)' }}>ROI</span>
                        </div>
                        <p className="text-lg font-bold font-numbers" style={{ 
                          color: selectedClosing.financial_data.roi_mensal >= 0 
                            ? 'var(--color-primary)' 
                            : 'var(--color-danger)' 
                        }}>
                          {selectedClosing.financial_data.roi_mensal?.toFixed(1) || 0}%
                        </p>
                        <p className="text-xs mt-1" style={{ color: 'var(--app-text-secondary)' }}>
                          Invest: {formatCurrency(selectedClosing.financial_data.capital_investido)}
                        </p>
                      </div>
                    </div>
                  )}
                  
                  {/* AI Analysis Content */}
                  <div 
                    className="prose prose-invert max-w-none p-6 rounded-xl border"
                    style={{ 
                      backgroundColor: 'color-mix(in srgb, var(--app-text-primary) 2%, transparent)',
                      borderColor: 'var(--app-border)'
                    }}
                  >
                    <ReactMarkdown
                      components={{
                        h1: ({node, ...props}) => <h1 className="text-2xl font-heading font-bold mb-4" style={{ color: 'var(--color-primary)' }} {...props} />,
                        h2: ({node, ...props}) => <h2 className="text-xl font-heading font-semibold mt-6 mb-3" style={{ color: 'var(--color-secondary)' }} {...props} />,
                        h3: ({node, ...props}) => <h3 className="text-lg font-semibold mt-4 mb-2" style={{ color: 'var(--app-text-primary)' }} {...props} />,
                        p: ({node, ...props}) => <p className="mb-3 leading-relaxed" style={{ color: 'var(--app-text-secondary)' }} {...props} />,
                        ul: ({node, ...props}) => <ul className="list-disc pl-5 mb-4 space-y-1" {...props} />,
                        ol: ({node, ...props}) => <ol className="list-decimal pl-5 mb-4 space-y-1" {...props} />,
                        li: ({node, ...props}) => <li style={{ color: 'var(--app-text-secondary)' }} {...props} />,
                        strong: ({node, ...props}) => <strong style={{ color: 'var(--app-text-primary)' }} {...props} />,
                        blockquote: ({node, ...props}) => (
                          <blockquote 
                            className="border-l-4 pl-4 py-2 my-4 italic"
                            style={{ borderColor: 'var(--color-primary)', color: 'var(--app-text-secondary)' }}
                            {...props}
                          />
                        ),
                        hr: ({node, ...props}) => <hr className="my-6" style={{ borderColor: 'var(--app-border)' }} {...props} />,
                      }}
                    >
                      {selectedClosing.ai_analysis}
                    </ReactMarkdown>
                  </div>
                  
                  {/* Timestamp */}
                  <p className="text-xs text-right" style={{ color: 'var(--app-text-secondary)' }}>
                    Gerado em: {new Date(selectedClosing.created_at).toLocaleString('pt-BR')}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default FinancialDirectorPage;
